// client/src/pages/PapabileDuJourArchivePage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PageWrapper from '../components/PageWrapper';
import '../styles/PapabileDuJourArchivePage.css';

const PapabileDuJourArchivePage = () => {
  const [papabiles, setPapabiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  
  // Image de fallback pour les cardinaux sans photo
  const defaultImage = '/images/cardinal-default.png';
  
  useEffect(() => {
    const fetchArchive = async () => {
      try {
        setLoading(true);
        
        // Récupérer tous les papabiles du jour passés
        const response = await axios.get('/api/papabile-jour/recent?limit=100');
        setPapabiles(response.data);
        
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des archives:', err);
        setError('Erreur lors du chargement des données');
        setLoading(false);
      }
    };
    
    fetchArchive();
  }, []);
  
  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl, cardinalId) => {
    if (!originalUrl || imageErrors[cardinalId]) return defaultImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = (cardinalId) => {
    console.log(`Erreur de chargement d'image pour cardinal ${cardinalId}, utilisation de l'image par défaut`);
    setImageErrors(prev => ({
      ...prev,
      [cardinalId]: true
    }));
  };
  
  // Grouper les papabiles par année
  const papabilesByYear = papabiles.reduce((acc, papabile) => {
    const year = new Date(papabile.dateAffichage).getFullYear();
    
    if (!acc[year]) {
      acc[year] = [];
    }
    
    acc[year].push(papabile);
    return acc;
  }, {});
  
  // Trier les années par ordre décroissant
  const sortedYears = Object.keys(papabilesByYear).sort((a, b) => b - a);
  
  if (loading) return <div className="loading">Chargement des archives...</div>;
  if (error) return <div className="error">{error}</div>;
  
  return (
    <PageWrapper pageType="papabile-jour-archive">
      <div className="papabile-archive-page">
        <div className="archive-header">
          <h1>Archives des Papabili du Jour</h1>
          <p className="archive-subtitle">
            Retrouvez tous les cardinaux papabili mis en avant quotidiennement
          </p>
          
          <Link to="/papabile-du-jour" className="archive-back-link">
            ← Retour au papabile du jour
          </Link>
        </div>
        
        <div className="archive-content">
          {sortedYears.length === 0 ? (
            <div className="no-archives">Aucune archive disponible</div>
          ) : (
            sortedYears.map(year => (
              <div key={year} className="archive-year-section">
                <h2 className="archive-year">{year}</h2>
                
                <div className="archive-grid">
                  {papabilesByYear[year].map(papabile => (
                    <Link 
                      to={`/papabile-du-jour/${papabile.slug}`} 
                      key={papabile._id}
                      className="archive-card"
                    >
                      <div className="archive-date">
                        {format(new Date(papabile.dateAffichage), 'd MMMM yyyy', { locale: fr })}
                      </div>
                      <div className="archive-card-content">
                        <div className="archive-photo">
                          <img 
                            src={getApiImageUrl(papabile.cardinalId.photo, papabile.cardinalId._id)} 
                            alt={papabile.cardinalId.nomComplet} 
                            onError={() => handleImageError(papabile.cardinalId._id)}
                          />
                        </div>
                        <div className="archive-info">
                          <h3 className="archive-name">{papabile.cardinalId.nomComplet}</h3>
                          <p className="archive-subtitle">{papabile.cardinalId.titre || papabile.cardinalId.ordre}</p>
                          <div className="archive-meta">
                            <span>{papabile.cardinalId.paysService}</span>
                            <span>{papabile.cardinalId.age} ans</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </PageWrapper>
  );
};

export default PapabileDuJourArchivePage;